import React, { useState } from 'react';
import {
  Button, Modal, Form, InputNumber, Switch, Alert,
} from 'antd';
import { useSearchParams } from 'react-router-dom';
import { DownloadOutlined } from '@ant-design/icons';
import { createSearchParamsString } from '../../../../utils';
import { useMessageError } from '../../../../hooks/common';
import { useAuth } from '../../../../store/auth';
import { isRoleEnough } from '../../../../enums/user';
import { useUsersExportCSV } from '../../../../hooks/api/users';
import downloadCSV from '../../../../utils/csv';

function CsvExportButton() {
  const { user } = useAuth();
  const usersExportCSV = useUsersExportCSV();
  const [searchParams] = useSearchParams();

  /** State for modal visibility and form fields */
  const [isOpen, setIsOpen] = useState(false);
  const [limit, setLimit] = useState<number | null>(100);
  const [isAdvertisementConsent, setIsAdvertisementConsent] = useState<boolean>(false);

  /** State to cache the last downloaded CSV data and its corresponding request parameters */
  const [csvData, setCsvData] = useState<string | null>(null);
  const [lastRequestParams, setLastRequestParams] = useState<string | null>(null);

  useMessageError([usersExportCSV]);

  const showModal = () => {
    setIsOpen(true);
  };

  const handleClose = () => {
    setIsOpen(false);
    setLimit(100);
    setIsAdvertisementConsent(false);
    /** Clear cached data when the modal is closed */
    setCsvData(null);
    setLastRequestParams(null);
  };

  const handleDownload = () => {
    /** Gathers existing search params and add the new ones from the modal form */
    const exportParams: { [key: string]: any } = {
      ...Object.fromEntries(searchParams.entries()),
      limit,
      isAdvertisementConsent,
      current: undefined,
      pageSize: undefined,
    };

    const searchString = createSearchParamsString(exportParams);

    /** If data has already been fetched with the same parameters, just trigger the download again */
    if (csvData && lastRequestParams === searchString) {
      downloadCSV(csvData, `users-${searchString}`);

      return;
    }

    usersExportCSV.fetch(undefined, searchString)
      .then((res) => {
        if (res && typeof res === 'string') {
          setCsvData(res);
          setLastRequestParams(searchString);
          downloadCSV(res, `export-${searchString}.csv`);
        }
      });
  };

  if (!isRoleEnough(user?.role, 'admin')) {
    return null;
  }

  return (
    <>
      <Button type="primary" onClick={showModal} icon={<DownloadOutlined />}>
        Export CSV
      </Button>
      <Modal
        title={(
          <span>
            <DownloadOutlined />
            {' '}
            Export Users to CSV
          </span>
        )}
        open={isOpen}
        onCancel={handleClose}
        centered
        footer={[
          <Button key="back" onClick={handleClose}>
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={usersExportCSV.loading}
            onClick={handleDownload}
          >
            Download
          </Button>,
        ]}
      >
        <Alert
          message="By clicking 'Download', you will export a CSV file using the currently selected filters from the
          list, along with any additional filters set in this modal."
          type="info"
          showIcon
          style={{ marginBottom: '16px' }}
        />
        <Form layout="horizontal" onFinish={handleDownload}>
          <Form.Item label="Limit">
            <InputNumber
              value={limit}
              onChange={(value) => {
                setLimit(value);
                /** Invalidate cache if settings change */
                setCsvData(null);
              }}
              style={{ width: '100%' }}
              min={1}
              max={100000}
              placeholder="Enter the number of records to export"
              step={10}
            />
          </Form.Item>
          <Form.Item label="Advertisements Consent agreement" valuePropName="checked">
            <Switch
              checked={isAdvertisementConsent}
              onChange={(checked) => {
                setIsAdvertisementConsent(checked);
                setCsvData(null);
              }}
              checkedChildren="Activated"
              unCheckedChildren="Inactive"
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}

export default CsvExportButton;
